<!-- Time navigation toolbar -->
<div class="time-navigation-toolbar">
    <div class="toolbar-left">
        <p-selectbutton [(ngModel)]="selectedLogLevel" [options]="logLevelOptions" optionLabel="label" optionValue="value" size="small" [allowEmpty]="false" />
        <p-button icon="pi pi-calendar" label="Jump to Time" (onClick)="toggleDatePicker()" severity="contrast" [outlined]="true" size="small" />
        @if (isHistoricalView()) {
        <p-button icon="pi pi-fast-forward" label="Jump to Latest" (onClick)="jumpToLatest()" severity="primary" size="small" />
        }
    </div>

    <div class="toolbar-right">
        @if (isHistoricalView()) {
        <span class="historical-indicator">Viewing Historical Logs</span>
        } @else {
        <span class="streaming-indicator">
            Streaming Logs
            <span class="streaming-dot"></span>
        </span>
        }

        <p-iconfield iconPosition="left" class="search-input">
            <p-inputicon class="pi pi-search" />
            <input type="text" pInputText [(ngModel)]="searchQuery" (ngModelChange)="onSearchChange($event)" placeholder="Search logs..." />
            @if (searchQuery()) {
            <p-inputicon (click)="clearSearch()" class="clear-icon pi pi-times" />
            }
        </p-iconfield>

        <p-button icon="pi pi-download" label="Export" (onClick)="openExportDialog()" severity="secondary" [outlined]="true" size="small" pTooltip="Export logs to file" tooltipPosition="top" />
    </div>
</div>

<!-- Date picker dialog -->
<p-dialog [(visible)]="showDatePicker" [modal]="true" [closable]="false" [draggable]="false" [resizable]="false" header="Jump to Timestamp" [style]="{width: 'auto'}">
    <p-datepicker [(ngModel)]="selectedDateTime" [showTime]="true" [showSeconds]="true" [minDate]="logTimeRange().earliest || undefined" [maxDate]="logTimeRange().latest || undefined" [inline]="true" />
    <ng-template #footer>
        <p-button label="Cancel" (onClick)="showDatePicker.set(false)" [text]="true" severity="secondary" />
        <p-button label="Jump to Time" (onClick)="jumpToTimestamp(selectedDateTime())" icon="pi pi-calendar" />
    </ng-template>
</p-dialog>

@defer (when !initialLoading()) {
<div class="logs-container" #logsContainer (scroll)="onScroll()">
    <div class="top-fade"></div>
    <div class="logs">
        @for (log of filteredLogs(); track log.id) {
        <div class="log-entry" [ngClass]="getLogClasses(log)" (mouseenter)="onLogMouseEnter(log)" (mouseleave)="onLogMouseLeave()" (click)="onLogClick($event, log)">
            <span class="timestamp">{{ log.timestamp | date: 'dd/MM/yy HH:mm:ss.SSS' }}</span>
            <span class="level">[<span class="level-text">{{ log.level }}</span>]</span>
            <span class="message">{{ log.message }}</span>
            @if (log.fileName) {
            <span class="type">[{{ log.fileName }}:{{ log.lineNumber }}]</span>
            } @else if (log.category) {
            <span class="type">[{{ log.category }}]</span>
            }
        </div>
        @if (log.stack) {
        <div class="log-entry stack-trace">
            <pre>{{ log.stack }}</pre>
        </div>
        } } @if (filteredLogs().length === 0 && logs().length > 0) {
        <div class="empty">No logs match the selected filter.</div>
        } @else if (logs().length === 0) {
        <div class="empty">No logs yet.</div>
        }
    </div>
</div>
} @placeholder(minimum 1000ms) {
<div class="logs-loading">
    @for (r of [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19]; track r) {
    <div class="skeleton-row"></div>
    }
    <div class="loading-hint">Loading logs…</div>
</div>
}

<!-- Export dialog -->
<p-dialog header="Export Logs" [(visible)]="showExportDialog" [modal]="true" [style]="{ width: '480px', maxWidth: '95vw' }" [closable]="!exporting()" [appendTo]="'body'">
    <div class="export-form">
        <div class="export-field">
            <label>From</label>
            <p-datepicker [(ngModel)]="exportFromDate" [showTime]="true" [showSeconds]="true" [showIcon]="true" dateFormat="yy-mm-dd" [appendTo]="'body'" class="w-full" [disabled]="exporting()" />
        </div>
        <div class="export-field">
            <label>To</label>
            <p-datepicker [(ngModel)]="exportToDate" [showTime]="true" [showSeconds]="true" [showIcon]="true" dateFormat="yy-mm-dd" [appendTo]="'body'" class="w-full" [disabled]="exporting()" />
        </div>
        <div class="export-field">
            <label>Minimum Level</label>
            <p-select [options]="logLevelOptions" optionLabel="label" optionValue="value" [(ngModel)]="exportLevelFilter" [style]="{ width: '100%' }" [disabled]="exporting()" />
        </div>
        @if (exporting()) {
        <div class="export-progress">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Fetching logs… {{ exportProgress()?.fetched?.toLocaleString() ?? 0 }} entries retrieved</span>
        </div>
        }
    </div>
    <ng-template #footer>
        <p-button label="Cancel" [text]="true" severity="secondary" (onClick)="showExportDialog.set(false)" [disabled]="exporting()" />
        <p-button label="Download logs.txt" icon="pi pi-download" (onClick)="exportLogs()" [loading]="exporting()" [disabled]="!exportFromDate || !exportToDate" />
    </ng-template>
</p-dialog>

<!-- Popover for log details -->
<p-popover #logPopover [dismissable]="true" (onHide)="onPopoverHide()">
    <ng-template #content>
        @if (selectedLog(); as log) {
        <div class="log-details">
            <h3 class="log-details-title">Log Entry Details</h3>
            <div class="log-details-content">
                @for (key of getLogKeys(log); track key) {
                <div class="log-detail-row">
                    <span class="log-detail-key">{{ key }}:</span>
                    <span class="log-detail-value">{{ formatLogValue(log[key], key) }}</span>
                </div>
                }
            </div>
        </div>
        }
    </ng-template>
</p-popover>
